import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  NumberInput,
  Modal,
  Pagination,
  Loader,
  Center,
  Text,
  ActionIcon,
  Badge,
  ThemeIcon,
  Card,
} from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconSearch, IconFolder } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import ConfirmModal from "@/components/ConfirmModal";

export default function ProjectList() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [maxImageWidth, setMaxImageWidth] = useState<number>(1920);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.project.list({ page, limit: 20, search: search || undefined });
      setProjects(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setName(""); setMaxImageWidth(1920); setModalOpen(true); };
  const openEdit = (p: any) => { setEditId(p._id); setName(p.name); setMaxImageWidth(p.maxImageWidth || 1920); setModalOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) await api.project.update(editId, { name, maxImageWidth });
      else await api.project.create({ name, maxImageWidth });
      notifications.show({ title: "Амжилттай", message: editId ? "Шинэчлэгдлээ" : "Үүсгэгдлээ", color: "green" });
      setModalOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.project.remove(deleteId);
      notifications.show({ title: "Амжилттай", message: "Устгагдлаа", color: "green" });
      setDeleteId(null);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally { setDeleting(false); }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Төслүүд</Title>
          <Badge variant="light" size="lg">{total}</Badge>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="sm">Төсөл нэмэх</Button>
      </Group>

      <TextInput placeholder="Нэрээр хайх..." leftSection={<IconSearch size={16} />} value={search} onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }} w={300} size="sm" />

      {loading ? (
        <Center h={300}><Loader /></Center>
      ) : projects.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl"><IconFolder size={24} /></ThemeIcon>
            <Text size="sm" c="dimmed">Төсөл байхгүй</Text>
            <Button variant="light" size="xs" onClick={openCreate}>Төсөл нэмэх</Button>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={500}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Max Width</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={100}>Үйлдэл</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {projects.map((p, idx) => (
                  <Table.Tr key={p._id}>
                    <Table.Td c="dimmed" fz="sm">{(page - 1) * 20 + idx + 1}</Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{p.name}</Text></Table.Td>
                    <Table.Td><Badge variant="light" size="sm">{p.maxImageWidth || 1920}px</Badge></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(p.createdAt)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="light" radius="md" color="blue" onClick={() => openEdit(p)}><IconEdit size={14} /></ActionIcon>
                        <ActionIcon size="sm" variant="light" radius="md" color="red" onClick={() => setDeleteId(p._id)}><IconTrash size={14} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Card>
      )}

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={<Text fw={600}>{editId ? "Төсөл засах" : "Төсөл нэмэх"}</Text>} size="sm" centered>
        <Stack>
          <TextInput label="Төслийн нэр" placeholder="Нэр оруулах" value={name} onChange={(e) => { const v = e.currentTarget.value; setName(v); }} required size="sm" />
          <NumberInput label="Зургийн max өргөн (px)" value={maxImageWidth} onChange={(v) => setMaxImageWidth(Number(v) || 1920)} min={320} max={8000} step={100} size="sm" description="Хуулахдаа энэ өргөнөөс илүү бол resize хийнэ" />
          <Button onClick={handleSave} loading={saving} size="sm">Хадгалах</Button>
        </Stack>
      </Modal>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </Stack>
  );
}
